import { SessionVideoSourceStatus, VideoSourceInfo } from '../types';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

const V1_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

const COOKIE_SESSION_SENTINEL = 'cookie-session';

function isPublicShareToken(token?: string): token is string {
  if (!token) return false;
  if (token === COOKIE_SESSION_SENTINEL) return false;
  return token.length > 40 && !token.includes('.');
}

export interface SessionContextItem {
  sessionId: string;
  filename: string;
  title: string;
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  speakerCount: number;
  speakerNames: string[];
  wordCount: number;
  includedInContext: boolean;
  addedAt: Date;
  transcript?: string; // Loaded on demand
  ttmlFiles?: Array<{ name: string; content: string }>; // Latest TTML from session
}

export interface ChatConversation {
  id: string;
  name: string;
  messages: Array<{ role: string; content: string; timestamp: Date }>;
  isPinned?: boolean;
  ownerId?: string; // User token hash or browser ID for public shares - used to isolate conversations
  isPublicShare?: boolean; // Whether this conversation was created by a public share user
  chatSettings?: {
    selectedModel?: string;
    useStorytellingContext?: boolean;
    enableWebSearch?: boolean;
    textOnlyTimeReferences?: boolean;
    includeTTMLInContext?: boolean;
    includeTopicMapInContext?: boolean;
    includeCommentsInContext?: boolean;
    includeYouTubeStatsInContext?: boolean;
    uploadedFiles?: Array<{
      id: string;
      name: string;
      type: 'text' | 'pdf' | 'image';
      content?: string;
      size: number;
      mimeType: string;
      uploadedAt: string;
      s3Key?: string;
      isLoading?: boolean;
      includedInContext: boolean;
    }>;
    fileContextToggles?: Record<string, boolean>;
    sessionContext?: SessionContextItem[];
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface SessionData {
  sessionId: string;
  transcriptionChecksum: string;
  selectedTranscriptChecksum?: string | null;
  filename: string;
  title?: string; // AI-generated or user-edited title
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  transcriptionData: any;
  speakerLabels: any;
  finalTranscript: string;
  currentTranscript: string;
  speakerSegments: any;
  distinctSpeakers: number[];
  generatedTtmlFiles: Array<{ name: string; content: string; filename?: string; generatedAt?: Date; checksum?: string }>;
  chatMessages: Array<{ role: string; content: string }>; // Legacy - kept for backward compatibility
  uploadedFiles: Array<{
    id: string;
    name: string;
    type: 'text' | 'pdf' | 'image';
    content?: string; // Optional - may need to be fetched from S3
    size: number;
    mimeType: string;
    uploadedAt: string;
    s3Key?: string; // S3 storage key if available
    includedInContext?: boolean; // Optional - defaults to true if not specified
  }>;
  sessionContext: SessionContextItem[]; // Other sessions included in chat context
  conversations?: ChatConversation[]; // Multiple conversations per session
  activeConversationId?: string; // Currently active conversation
  chatSettings?: {
    selectedModel?: string;
    useStorytellingContext?: boolean;
    enableWebSearch?: boolean;
    textOnlyTimeReferences?: boolean;
    includeTTMLInContext?: boolean;
    includeTopicMapInContext?: boolean;
    includeCommentsInContext?: boolean;
    includeYouTubeStatsInContext?: boolean;
  }; // Chat interface settings - legacy
  selectedTtmlId?: string; // ID/checksum of the selected TTML file
  linkedYoutubeUrl?: string; // YouTube URL linked for video player integration (legacy)
  videoUrl?: string; // Effective playback URL (explicit YouTube or hosted AWS playback)
  videoSource?: 'youtube' | 'dropbox' | 'file' | null; // Effective playback source
  customThumbnailS3Key?: string | null;
  customThumbnailUrl?: string | null;
  customThumbnailTimeSeconds?: number | null;
  resolvedThumbnailUrl?: string | null;
  audioStatus?: {
    hasS3Audio: boolean;
    s3MigrationStatus: string;
    hasLocalCache: boolean;
    isOrphan: boolean;
  };
  audioUrl?: string; // Presigned URL for audio playback
  youtubeDescription?: string; // User-editable YouTube description
  youtubeTitle?: string | null;
  youtubeTitleUpdatedAt?: string | null;
  youtubeTitleSync?: YouTubeTitleSyncState | null;
  family?: EpisodeFamilyDetail | null;
  isDeprecated?: boolean;
  deprecatedAt?: string | null;
  deprecatedReason?: string | null;
  createdAt: Date;
  updatedAt: Date;
  duration?: number; // Session duration in seconds
  // Production metadata (private, not in public shares)
  dropboxVideoUrl?: string | null;
  videoFilename?: string | null;
  dropboxDrpUrl?: string | null;
  timelineName?: string | null;
  localVideoPath?: string | null;
  productionNotes?: string | null;
  videoSourceStatus?: SessionVideoSourceStatus | null;
  videoFileHash?: string | null;
  videoFileSize?: number | null;
  videoFrameRate?: number | null;
  videoDuration?: number | null;
  publishedToHomepage?: boolean;
}

export interface YouTubeTitleSyncState {
  enabled: boolean;
  nextAttemptAt: string | null;
  backoffStep: number;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
}

export interface EpisodeFamilyMember {
  sessionId: string;
  versionNumber: number | null;
  title: string;
  filename?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deprecated?: boolean;
  isHead?: boolean;
  isCurrent?: boolean;
}

export interface EpisodeFamilySummary {
  slug: string;
  title: string | null;
  versionNumber: number | null;
  versionCount: number;
  headSessionId: string | null;
  isHead: boolean;
}

export interface EpisodeFamilyDetail extends EpisodeFamilySummary {
  members: EpisodeFamilyMember[];
}

export interface EpisodeFamilyListItem {
  slug: string;
  title: string | null;
  versionCount: number;
  headSessionId: string | null;
  showSlug: string;
  showTitle: string;
  members: EpisodeFamilyMember[];
}

export interface SessionStorageItem {
  sessionId: string;
  title: string;
  filename: string | null;
  showSlug: string;
  showTitle: string;
  publishedToHomepage?: boolean;
  familySlug?: string | null;
  familyTitle?: string | null;
  familyVersionNumber?: number | null;
  familyMemberCount?: number | null;
  familyLatestSessionId?: string | null;
  familyLatestTitle?: string | null;
  familyLatestVersionNumber?: number | null;
  familyLatestHasYoutubeVideo?: boolean;
  familyLatestHasHostedVideo?: boolean;
  familyLatestPublishedToHomepage?: boolean;
  newerFamilyVersionSessionId?: string | null;
  newerFamilyVersionTitle?: string | null;
  newerFamilyVersionNumber?: number | null;
  newerFamilyVersionHasYoutubeVideo?: boolean;
  newerFamilyVersionHasHostedVideo?: boolean;
  newerFamilyVersionPublishedToHomepage?: boolean;
  arrangementShouldUpgradeToLatestFamilyVersion?: boolean;
  arrangementRefs?: Array<{
    arrangementId: string;
    arrangementTitle: string;
    arrangementStatus?: string | null;
  }>;
  hasYoutubeVideo: boolean;
  hasAwsSource: boolean;
  hasProxyVideo: boolean;
  hasHostedMedia: boolean;
  hasPlayableVideo: boolean;
  proxyStatus: string | null;
  sourceBytes: number;
  proxyBytes: number;
  totalBytes: number;
  estimatedMonthlyStorageCostUsd: number;
  arrangementRefCount: number;
  proxyDeleteEligible: boolean;
  proxyCleanupCandidate: boolean;
  hostedMediaDeleteEligible: boolean;
  cleanupReason: string | null;
  cleanupScope?: 'source_and_proxy' | 'source' | 'proxy' | 'none';
}

export interface SessionStorageSummary {
  summary: {
    totalSessions: number;
    sessionsWithPlayableVideo: number;
    sessionsWithYoutubeVideo: number;
    sessionsWithAwsSource: number;
    sessionsWithProxyVideo: number;
    sessionsWithHostedMedia: number;
    totalSourceBytes: number;
    totalProxyBytes: number;
    totalBytes: number;
    estimatedMonthlyStorageCostUsd: number;
    proxyDeleteEligibleCount: number;
    proxyCleanupCandidateCount: number;
    proxyCleanupCandidateBytes: number;
    hostedMediaDeleteEligibleCount: number;
    hostedMediaDeleteEligibleBytes: number;
  };
  sessions: SessionStorageItem[];
}

export interface CreateSessionRequest {
  transcriptionChecksum: string;
  filename: string;
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  transcriptionData: any;
  speakerLabels: any;
  finalTranscript: string;
  currentTranscript: string;
  speakerSegments: any;
  distinctSpeakers: number[];
  dropboxVideoUrl?: string | null;
  videoFilename?: string | null;
  dropboxDrpUrl?: string | null;
  timelineName?: string | null;
  localVideoPath?: string | null;
  productionNotes?: string | null;
  videoSourceInfo?: VideoSourceInfo;
  autoPrepareVideoSource?: boolean;
  showSlug: string;
  showTitle: string;
  familySlug?: string | null;
  familyTitle?: string | null;
  familyVersionNumber?: number | null;
}

export interface UpdateSessionRequest {
  sessionId: string;
  transcriptionData?: any;
  speakerLabels?: any;
  finalTranscript?: string;
  currentTranscript?: string;
  generatedTtmlFiles?: Array<{ name: string; content: string; filename?: string; generatedAt?: Date; checksum?: string }>;
  chatMessages?: Array<{ role: string; content: string }>;
  uploadedFiles?: Array<{
    id: string;
    name: string;
    type: 'text' | 'pdf' | 'image';
    content?: string;
    size: number;
    mimeType: string;
    uploadedAt: string;
    s3Key?: string;
    includedInContext: boolean;
  }>;
  sessionContext?: SessionContextItem[];
  chatSettings?: {
    selectedModel?: string;
    useStorytellingContext?: boolean;
    enableWebSearch?: boolean;
    textOnlyTimeReferences?: boolean;
    includeTTMLInContext?: boolean;
    includeTopicMapInContext?: boolean;
    includeCommentsInContext?: boolean;
    includeYouTubeStatsInContext?: boolean;
  }; // Chat interface settings
  conversations?: ChatConversation[]; // Multiple conversations per session
  activeConversationId?: string; // Currently active conversation
  selectedTtmlId?: string | null; // Selected TTML file ID/checksum
  youtubeDescription?: string; // User-editable YouTube description
  showSlug?: string;
  showTitle?: string;
  familySlug?: string | null;
  familyTitle?: string | null;
  familyVersionNumber?: number | null;
}

export class SessionService {
  private static async request<T>(endpoint: string, options: RequestInit = {}, token?: string): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  static async createSession(request: CreateSessionRequest, token?: string): Promise<{ sessionId: string; isExisting?: boolean; message?: string }> {
    return this.request<{ sessionId: string; isExisting?: boolean; message?: string }>('/sessions', {
      method: 'POST',
      body: JSON.stringify(request),
    }, token);
  }

  static async getSession(sessionId: string, token?: string): Promise<SessionData> {
    if (isPublicShareToken(token)) {
      // Use the public share API endpoint
      const response = await fetch(`${API_BASE_URL}/public/share/${token}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      return data.session as SessionData;
    }
    
    // Regular authenticated request
    return this.request<SessionData>(`/sessions/${sessionId}`, {}, token);
  }

  static async updateSession(request: UpdateSessionRequest, token?: string): Promise<{ success: boolean }> {
    console.log('[SESSION-SERVICE] updateSession called with:', {
      sessionId: request.sessionId,
      hasGeneratedTtmlFiles: !!(request as any).generatedTtmlFiles,
      ttmlFilesCount: (request as any).generatedTtmlFiles?.length,
      ttmlFiles: (request as any).generatedTtmlFiles?.map((f: any) => ({
        name: f.name,
        checksum: f.checksum,
        hasContent: !!f.content,
        contentLength: f.content?.length
      }))
    });
    
    const result = await this.request<{ success: boolean }>(`/sessions/${request.sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }, token);
    
    console.log('[SESSION-SERVICE] updateSession result:', result);
    return result;
  }

  static async listSessions(
    token?: string,
    search?: string,
    limit?: number,
    filter?: 'all' | 'youtube' | 'video' | 'shared' | 'deprecated',
    options?: {
      show?: string;
      contentSource?: 'owned' | 'external' | 'all';
      contentVisibility?: 'default' | 'internal' | 'all';
      includeExternal?: boolean;
    }
  ): Promise<Array<{
    sessionId: string;
    filename: string;
    title: string;
    showSlug: string;
    showTitle: string;
    family?: EpisodeFamilySummary | null;
    contentSource: 'owned' | 'external';
    contentVisibility: 'default' | 'internal';
    tags: string[];
    isYouTubeMode: boolean;
    youtubeUrl?: string;
    videoUrl?: string;
    videoSource?: 'youtube' | 'dropbox' | 'file' | null;
    speakerCount: number;
    speakerNames: string[];
    wordCount: number;
    shareLinksCount: number;
    publishedToHomepage: boolean;
    isDeprecated: boolean;
    deprecatedAt?: string | null;
    deprecatedReason?: string | null;
    createdAt: Date;
    updatedAt: Date;
    timeAgo: string;
  }>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit) params.append('limit', limit.toString());
    if (filter && filter !== 'all') params.append('filter', filter);
    if (options?.show && options.show !== 'all') params.append('show', options.show);
    if (options?.contentSource && options.contentSource !== 'all') params.append('contentSource', options.contentSource);
    if (options?.contentVisibility && options.contentVisibility !== 'all') params.append('visibility', options.contentVisibility);
    if (options?.includeExternal) params.append('includeExternal', 'true');

    const queryString = params.toString();
    const endpoint = queryString ? `/sessions?${queryString}` : '/sessions';

    return this.request<Array<{
      sessionId: string;
      filename: string;
      title: string;
      showSlug: string;
      showTitle: string;
      family?: EpisodeFamilySummary | null;
      contentSource: 'owned' | 'external';
      contentVisibility: 'default' | 'internal';
      tags: string[];
      isYouTubeMode: boolean;
      youtubeUrl?: string;
      videoUrl?: string;
      videoSource?: 'youtube' | 'dropbox' | 'file' | null;
      speakerCount: number;
      speakerNames: string[];
      wordCount: number;
      shareLinksCount: number;
      publishedToHomepage: boolean;
      isDeprecated: boolean;
      deprecatedAt?: string | null;
      deprecatedReason?: string | null;
      createdAt: Date;
      updatedAt: Date;
      timeAgo: string;
    }>>(endpoint, {}, token);
  }

  static async getSessionStorageSummary(
    token?: string,
    search?: string,
    filter?: 'all' | 'youtube' | 'video' | 'shared' | 'deprecated',
    options?: {
      show?: string;
      contentSource?: 'owned' | 'external' | 'all';
      contentVisibility?: 'default' | 'internal' | 'all';
      includeExternal?: boolean;
    }
  ): Promise<SessionStorageSummary> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (filter && filter !== 'all') params.append('filter', filter);
    if (options?.show && options.show !== 'all') params.append('show', options.show);
    if (options?.contentSource && options.contentSource !== 'all') params.append('contentSource', options.contentSource);
    if (options?.contentVisibility && options.contentVisibility !== 'all') params.append('visibility', options.contentVisibility);
    if (options?.includeExternal) params.append('includeExternal', 'true');
    const queryString = params.toString();
    const endpoint = queryString ? `/sessions/storage-summary?${queryString}` : '/sessions/storage-summary';
    return this.request<SessionStorageSummary>(endpoint, {}, token);
  }

  static async deleteHostedVideoMediaBatch(
    sessionIds: string[],
    token?: string
  ): Promise<{
    deletedSessionIds: string[];
    skipped: Array<{ sessionId: string; reason: string }>;
    deletedSourceBytes: number;
    deletedProxyBytes: number;
    deletedTotalBytes: number;
  }> {
    return this.request('/sessions/video-source/hosted-media/delete-batch', {
      method: 'POST',
      body: JSON.stringify({ sessionIds }),
    }, token);
  }

  static async deleteSession(sessionId: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}`, {
      method: 'DELETE',
    }, token);
  }

  static async toggleDeprecation(
    sessionId: string,
    deprecated: boolean,
    reason?: string | null,
    token?: string
  ): Promise<{
    success: boolean;
    session: {
      sessionId: string;
      title: string | null;
      deprecated: boolean;
      deprecatedAt?: string | null;
      deprecatedReason?: string | null;
    };
  }> {
    return this.request(`/sessions/${sessionId}/deprecation`, {
      method: 'PATCH',
      body: JSON.stringify({ deprecated, reason: reason ?? null }),
    }, token);
  }

  static generateSessionUrl(sessionId: string): string {
    return `${window.location.origin}/session/${sessionId}`;
  }

  static async listEpisodeFamilies(token?: string): Promise<EpisodeFamilyListItem[]> {
    const response = await fetch(`${V1_API_BASE_URL}/episodes/families`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return (data.families || []) as EpisodeFamilyListItem[];
  }

  // File upload methods
  static async uploadFile(sessionId: string, file: File, token?: string): Promise<{ 
    success: boolean; 
    file: {
      id: string;
      name: string;
      type: 'text' | 'pdf' | 'image';
      size: number;
      mimeType: string;
      uploadedAt: string;
    }
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/upload`, {
      method: 'POST',
      body: formData,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  static async deleteFile(sessionId: string, fileId: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/files/${fileId}`, {
      method: 'DELETE',
    }, token);
  }

  static async getFile(sessionId: string, fileId: string, token?: string): Promise<{
    id: string;
    name: string;
    type: 'text' | 'pdf' | 'image';
    size: number;
    mimeType: string;
    uploadedAt: string;
    content: string; // base64
  }> {
    return this.request<{
      id: string;
      name: string;
      type: 'text' | 'pdf' | 'image';
      size: number;
      mimeType: string;
      uploadedAt: string;
      content: string;
    }>(`/sessions/${sessionId}/files/${fileId}`, {}, token);
  }

  static async updateTitle(sessionId: string, title: string, token?: string): Promise<{ success: boolean; title: string }> {
    return this.request<{ success: boolean; title: string }>(`/sessions/${sessionId}/title`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    }, token);
  }

  static async updateYoutubeUrl(
    sessionId: string,
    youtubeUrl: string | null,
    token?: string
  ): Promise<{ success: boolean; youtubeUrl: string | null; videoUrl?: string | null; videoSource?: string | null; youtubeTitleSync?: YouTubeTitleSyncState | null }> {
    return this.request<{ success: boolean; youtubeUrl: string | null; videoUrl?: string | null; videoSource?: string | null; youtubeTitleSync?: YouTubeTitleSyncState | null }>(`/sessions/${sessionId}/youtube-url`, {
      method: 'PUT',
      body: JSON.stringify({ youtubeUrl }),
    }, token);
  }

  static async updateVideoUrl(
    sessionId: string,
    videoUrl: string | null,
    token?: string
  ): Promise<{ success: boolean; videoUrl: string | null; videoSource: string | null; youtubeUrl?: string | null; youtubeTitleSync?: YouTubeTitleSyncState | null }> {
    return this.request<{ success: boolean; videoUrl: string | null; videoSource: string | null; youtubeUrl?: string | null; youtubeTitleSync?: YouTubeTitleSyncState | null }>(`/sessions/${sessionId}/video-url`, {
      method: 'PUT',
      body: JSON.stringify({ videoUrl }),
    }, token);
  }

  static async syncTitleFromYouTube(
    sessionId: string,
    options: { applyToSessionTitle?: boolean; enableAutoSync?: boolean } = {},
    token?: string
  ): Promise<{
    success: boolean;
    sessionId: string;
    title?: string | null;
    youtubeTitle: string;
    youtubeTitleUpdatedAt: string;
    youtubeTitleChanged: boolean;
    sessionTitleApplied: boolean;
    sessionTitleChanged: boolean;
    syncState: YouTubeTitleSyncState;
    youtubeTitleSync?: YouTubeTitleSyncState;
    youtubeTitleSyncEnabled?: boolean;
    source: string;
    videoId: string | null;
    channelTitle: string | null;
    publishedAt: string | null;
  }> {
    const syncResult = await this.request<{
      success: boolean;
      sessionId: string;
      youtubeTitle: string;
      youtubeTitleUpdatedAt: string;
      youtubeTitleChanged: boolean;
      sessionTitleApplied: boolean;
      sessionTitleChanged: boolean;
      syncState: YouTubeTitleSyncState;
      source: string;
      videoId: string | null;
      channelTitle: string | null;
      publishedAt: string | null;
    }>(`/sessions/${sessionId}/youtube-title/sync`, {
      method: 'POST',
      body: JSON.stringify({ applyToSessionTitle: options.applyToSessionTitle === true }),
    }, token);

    let youtubeTitleSync = syncResult.syncState;
    if (typeof options.enableAutoSync === 'boolean' && youtubeTitleSync.enabled !== options.enableAutoSync) {
      const syncToggleResult = await this.updateYoutubeTitleSync(sessionId, options.enableAutoSync, token);
      youtubeTitleSync = syncToggleResult.youtubeTitleSync;
    }

    return {
      ...syncResult,
      title: syncResult.sessionTitleApplied ? syncResult.youtubeTitle : null,
      youtubeTitleSync,
      youtubeTitleSyncEnabled: youtubeTitleSync.enabled,
    };
  }

  static async updateYoutubeTitleSync(
    sessionId: string,
    enabled: boolean,
    token?: string
  ): Promise<{ success: boolean; sessionId: string; youtubeTitleSync: YouTubeTitleSyncState }> {
    return this.request(`/sessions/${sessionId}/youtube-title-sync`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }, token);
  }

  static async getSessionInvalidations(
    sessionId: string,
    sinceVersion: number,
    token?: string
  ): Promise<{
    sessionId: string;
    version: number;
    scopes: string[];
    hasUpdates: boolean;
  }> {
    const query = new URLSearchParams({ sinceVersion: String(sinceVersion || 0) });
    return this.request(`/sessions/${sessionId}/invalidations?${query.toString()}`, {}, token);
  }

  // Homepage publish toggle
  static async toggleHomepagePublish(sessionId: string, published: boolean, token?: string): Promise<{ success: boolean; published: boolean }> {
    return this.request<{ success: boolean; published: boolean }>(`/sessions/${sessionId}/homepage-publish`, {
      method: 'PATCH',
      body: JSON.stringify({ published }),
    }, token);
  }

  // Production metadata update
  static async updateProductionMetadata(sessionId: string, data: {
    dropboxVideoUrl?: string | null;
    videoFilename?: string | null;
    dropboxDrpUrl?: string | null;
    timelineName?: string | null;
    localVideoPath?: string | null;
    productionNotes?: string | null;
  }, token?: string): Promise<{ success: boolean; productionMetadata?: Record<string, unknown> }> {
    return this.request<{ success: boolean; productionMetadata?: Record<string, unknown> }>(`/sessions/${sessionId}/production-metadata`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  }

  static async getVideoSourceStatus(sessionId: string, token?: string): Promise<SessionVideoSourceStatus> {
    return this.request<SessionVideoSourceStatus>(`/sessions/${sessionId}/video-source/status`, {}, token);
  }

  static async prepareVideoSource(
    sessionId: string,
    options: { force?: boolean; proxyMode?: string } = {},
    token?: string
  ): Promise<{ status?: string; stage?: string | null; jobId?: string | null; videoSourceStatus?: SessionVideoSourceStatus }> {
    return this.request<{ status?: string; stage?: string | null; jobId?: string | null; videoSourceStatus?: SessionVideoSourceStatus }>(
      `/sessions/${sessionId}/video-source/prepare`,
      {
        method: 'POST',
        body: JSON.stringify(options),
      },
      token
    );
  }

  static async deleteVideoSourceProxy(
    sessionId: string,
    token?: string
  ): Promise<{
    deletedObjects?: number;
    deletedBytes?: number;
    deletedFiles?: number;
    freedBytes?: number;
    deletedRecords?: number;
    videoSourceStatus?: SessionVideoSourceStatus;
  }> {
    return this.request(
      `/sessions/${sessionId}/video-source/proxy`,
      { method: 'DELETE' },
      token
    );
  }

  static async captureHostedThumbnailFrame(
    sessionId: string,
    timestampSeconds: number,
    token?: string
  ): Promise<{
    success: boolean;
    sessionId: string;
    customThumbnailS3Key: string | null;
    customThumbnailUrl: string | null;
    customThumbnailTimeSeconds: number | null;
    playbackKind?: string | null;
  }> {
    return this.request(
      `/sessions/${sessionId}/thumbnail/frame`,
      {
        method: 'POST',
        body: JSON.stringify({ timestampSeconds }),
      },
      token
    );
  }

  static async clearCustomThumbnail(
    sessionId: string,
    token?: string
  ): Promise<{
    success: boolean;
    sessionId: string;
    customThumbnailS3Key: null;
    customThumbnailUrl: null;
    customThumbnailTimeSeconds: null;
  }> {
    return this.request(
      `/sessions/${sessionId}/thumbnail`,
      { method: 'DELETE' },
      token
    );
  }

  // Session context management methods
  static async addSessionToContext(sessionId: string, contextSessionId: string, token?: string): Promise<{ success: boolean; contextItem: SessionContextItem }> {
    return this.request<{ success: boolean; contextItem: SessionContextItem }>(`/sessions/${sessionId}/context`, {
      method: 'POST',
      body: JSON.stringify({ contextSessionId }),
    }, token);
  }

  static async removeSessionFromContext(sessionId: string, contextSessionId: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/context/${contextSessionId}`, {
      method: 'DELETE',
    }, token);
  }

  static async toggleSessionContextInclusion(sessionId: string, contextSessionId: string, includedInContext: boolean, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/context/${contextSessionId}`, {
      method: 'PUT',
      body: JSON.stringify({ includedInContext }),
    }, token);
  }

  static async getSessionForContext(sessionId: string, token?: string): Promise<{
    sessionId: string;
    filename: string;
    title: string;
    isYouTubeMode: boolean;
    youtubeUrl?: string;
    speakerCount: number;
    speakerNames: string[];
    wordCount: number;
    transcript: string;
    ttmlFiles: Array<{ name: string; content: string }>;
  }> {
    return this.request<{
      sessionId: string;
      filename: string;
      title: string;
      isYouTubeMode: boolean;
      youtubeUrl?: string;
      speakerCount: number;
      speakerNames: string[];
      wordCount: number;
      transcript: string;
      ttmlFiles: Array<{ name: string; content: string }>;
    }>(`/sessions/${sessionId}/context-data`, {}, token);
  }

  // Conversation management methods
  static async createConversation(sessionId: string, name?: string, token?: string): Promise<{ conversationId: string; conversation: ChatConversation }> {
    return this.request<{ conversationId: string; conversation: ChatConversation }>(`/sessions/${sessionId}/conversations`, {
      method: 'POST',
      body: JSON.stringify({ name: name || 'New Conversation' }),
    }, token);
  }

  static async updateConversation(sessionId: string, conversationId: string, updates: Partial<ChatConversation>, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, token);
  }

  static async deleteConversation(sessionId: string, conversationId: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/conversations/${conversationId}`, {
      method: 'DELETE',
    }, token);
  }

  static async setActiveConversation(sessionId: string, conversationId: string, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/active-conversation`, {
      method: 'PUT',
      body: JSON.stringify({ conversationId }),
    }, token);
  }

  static async generateConversationName(sessionId: string, conversationId: string, token?: string): Promise<{ name: string }> {
    return this.request<{ name: string }>(`/sessions/${sessionId}/conversations/${conversationId}/generate-name`, {
      method: 'POST',
    }, token);
  }

  static async addMessageToConversation(sessionId: string, conversationId: string, message: { role: string; content: string }, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    }, token);
  }

  static async getNarrativeData(sessionId: string, token?: string): Promise<any | null> {
    try {
      if (isPublicShareToken(token)) {
        const response = await fetch(`${API_BASE_URL}/public/share/${token}/narrative`);
        if (!response.ok) throw new Error('Failed to fetch narrative data');
        const data = await response.json();
        return data.narrativeData || null;
      }
      
      const response = await this.request<{ narrativeData: any }>(`/sessions/${sessionId}/narrative`, {}, token);
      return response.narrativeData || null;
    } catch (error) {
      console.error('Error fetching narrative data:', error);
      return null;
    }
  }

  static async saveNarrativeData(sessionId: string, narrativeData: any, token?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/sessions/${sessionId}/narrative`, {
      method: 'PUT',
      body: JSON.stringify({ narrativeData }),
    }, token);
  }

  static async getNarrativeGenerationStatus(sessionId: string, token?: string): Promise<{status: string; startedAt?: string; error?: string}> {
    if (isPublicShareToken(token)) {
      const response = await fetch(`${API_BASE_URL}/public/share/${token}/narrative-status`);
      if (!response.ok) throw new Error('Failed to fetch narrative status');
      return await response.json();
    }
    
    return this.request<{status: string; startedAt?: string; error?: string}>(`/sessions/${sessionId}/narrative-status`, {}, token);
  }

  static async getInvalidations(
    sessionId: string,
    sinceVersion: number,
    token?: string
  ): Promise<{
    sessionId: string;
    version: number;
    scopes: string[];
    hasUpdates: boolean;
  }> {
    return this.request(`/sessions/${sessionId}/invalidations?sinceVersion=${encodeURIComponent(String(sinceVersion))}`, {}, token);
  }

  static async getNarrativeVersions(sessionId: string, token?: string): Promise<{versions: Array<{
    id: string;
    version_number: number;
    created_at: string;
    created_by: string;
    is_active: number;
    generation_prompt?: string;
  }>}> {
    return this.request<{versions: Array<any>}>(`/sessions/${sessionId}/narrative-versions`, {}, token);
  }

  static async createNarrativeVersion(sessionId: string, narrativeData: any, generationPrompt?: string, generationParams?: any, token?: string): Promise<{success: boolean; versionId: string; versionNumber: number}> {
    return this.request<{success: boolean; versionId: string; versionNumber: number}>(`/sessions/${sessionId}/narrative-versions`, {
      method: 'POST',
      body: JSON.stringify({ narrativeData, generationPrompt, generationParams }),
    }, token);
  }

  static async activateNarrativeVersion(sessionId: string, versionId: string, token?: string): Promise<{success: boolean}> {
    return this.request<{success: boolean}>(`/sessions/${sessionId}/narrative-versions/${versionId}/activate`, {
      method: 'PUT',
    }, token);
  }

  static async getNarrativeVersion(sessionId: string, versionId: string, token?: string): Promise<any> {
    return this.request<any>(`/sessions/${sessionId}/narrative-versions/${versionId}`, {}, token);
  }

  // ====================================================================================
  // ASYNC CHAT SERVICE METHODS
  // ====================================================================================

  static async createAsyncChatJob(
    sessionId: string,
    conversationId: string,
    prompt: string,
    model: string,
    settings?: {
      disableTimecodeLinks?: boolean;
      autoName?: boolean;
    },
    token?: string,
    systemPrompt?: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    uploadedFiles?: Array<{
      id: string;
      name: string;
      type: 'text' | 'pdf' | 'image';
      size: number;
      mimeType?: string;
    }>
  ): Promise<{
    success: boolean;
    jobId: string;
    status: string;
    createdAt: string;
  }> {
    return this.request<{
      success: boolean;
      jobId: string;
      status: string;
      createdAt: string;
    }>(`/sessions/${sessionId}/conversations/${conversationId}/async-message`, {
      method: 'POST',
      body: JSON.stringify({ prompt, model, settings, systemPrompt, conversationHistory, uploadedFiles }),
    }, token);
  }

  static async getActiveJobsForConversation(
    sessionId: string,
    conversationId: string,
    token?: string
  ): Promise<{
    success: boolean;
    jobs: Array<{
      jobId: string;
      status: 'pending' | 'running';
      prompt: string;
      createdAt: string;
      startedAt?: string;
    }>;
  }> {
    return this.request<{
      success: boolean;
      jobs: any[];
    }>(`/sessions/${sessionId}/conversations/${conversationId}/active-jobs`, {}, token);
  }

  static async getAsyncChatJobStatus(
    sessionId: string,
    jobId: string,
    token?: string
  ): Promise<{
    success: boolean;
    job: {
      jobId: string;
      sessionId: string;
      conversationId: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
      result?: string;
      streamedContent?: string;
      error?: string;
      createdAt: string;
      startedAt?: string;
      completedAt?: string;
      tokenUsage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
      batchId?: string;
      context?: {
        inputTokens: number;
        outputTokens: number;
        totalTokens: number;
        maxTokens: number;
        percentUsed: number;
        compactionCount: number;
        status: 'green' | 'yellow' | 'orange' | 'red';
      } | null;
    };
  }> {
    return this.request<{
      success: boolean;
      job: any;
    }>(`/sessions/${sessionId}/jobs/${jobId}/status`, {}, token);
  }

  static async cancelAsyncChatJob(
    sessionId: string,
    jobId: string,
    token?: string
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      message?: string;
      error?: string;
    }>(`/sessions/${sessionId}/jobs/${jobId}/cancel`, {
      method: 'POST',
    }, token);
  }

  static async createBatchChatJobs(
    sessionId: string,
    prompts: Array<{ prompt: string } | string>,
    model: string,
    settings?: {
      disableTimecodeLinks?: boolean;
      autoName?: boolean;
    },
    token?: string,
    systemPrompt?: string
  ): Promise<{
    success: boolean;
    batchId: string;
    jobs: Array<{ jobId: string; status: string; createdAt: string }>;
    totalJobs: number;
    conversations: Array<{ conversationId: string; prompt: string }>;
  }> {
    return this.request<{
      success: boolean;
      batchId: string;
      jobs: any[];
      totalJobs: number;
      conversations: any[];
    }>(`/sessions/${sessionId}/batch-generate`, {
      method: 'POST',
      body: JSON.stringify({ prompts, model, settings, systemPrompt }),
    }, token);
  }

  static async getBatchStatus(
    sessionId: string,
    batchId: string,
    token?: string
  ): Promise<{
    success: boolean;
    batchId: string;
    totalJobs: number;
    completed: number;
    failed: number;
    pending: number;
    running: number;
    isComplete: boolean;
    jobs: Array<{
      jobId: string;
      conversationId: string;
      status: string;
      result?: string;
      error?: string;
      completedAt?: string;
    }>;
  }> {
    return this.request<{
      success: boolean;
      batchId: string;
      totalJobs: number;
      completed: number;
      failed: number;
      pending: number;
      running: number;
      isComplete: boolean;
      jobs: any[];
    }>(`/sessions/${sessionId}/batch/${batchId}/status`, {}, token);
  }
}

export interface ArrangementItem {
  id: string;
  title: string;
  description: string;
  createdBy: string;
  previewToken: string;
  status: string;
  totalDurationSeconds: number;
  totalClips: number;
  storageBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface ArrangementStorageSummaryItem {
  id: string;
  title: string;
  status: string;
  totalBytes: number;
  arrangementManagedS3Bytes: number;
  sharedSourceVideoBytes: number;
  totalObjects: number;
  estimatedMonthlyCostUsd: number;
  estimatedMonthlyDeliveryCostUsd?: number;
  estimatedMonthlyTotalCostUsd?: number;
  deliveryCostModel?: {
    provider: 'cloudfront';
    monthlyReadMultiplier: number;
    requestsPerObject: number;
    dataTransferRatePerGb: number;
    requestRatePer10k: number;
    deliveredBytesPerRead: number;
    requestCountPerRead: number;
    estimatedTransferCostUsd: number;
    estimatedRequestCostUsd: number;
  };
  updatedAt: string;
}

export interface ArrangementsStorageSummary {
  totalBytes: number;
  totalObjects: number;
  estimatedMonthlyCostUsd: number;
  estimatedMonthlyDeliveryCostUsd?: number;
  estimatedMonthlyTotalCostUsd?: number;
  arrangements: ArrangementStorageSummaryItem[];
}

export async function listArrangements(token?: string): Promise<ArrangementItem[]> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${V1_API_BASE_URL}/arrangements`, { headers });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return data.arrangements;
}

export async function getArrangementsStorageSummary(token?: string): Promise<ArrangementsStorageSummary> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${V1_API_BASE_URL}/arrangements/storage/summary`, { headers });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function deleteArrangement(arrangementId: string, confirmation: string, token?: string): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${V1_API_BASE_URL}/arrangements/${arrangementId}`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ confirmation }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function purgeArrangementCache(arrangementId: string, token?: string): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${V1_API_BASE_URL}/arrangements/${arrangementId}/cache-purge`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export const sessionService = SessionService;
